/**
 * Start game button for host.
 *
 * Game starts when min_players have joined (from game config).
 * Ready status is for future Epic 3 mechanics.
 */

import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

interface StartButtonProps {
  disabled: boolean
  totalCount: number
  minPlayers?: number  // From game config, defaults to 2
  onClick: () => void
  /** Slimmer button carrying only the call to action; used where vertical space is scarce. */
  compact?: boolean
}

export function StartButton({
  disabled,
  totalCount,
  minPlayers = 2,
  onClick,
  compact = false,
}: StartButtonProps) {
  const { t } = useTranslation()
  const needMore = Math.max(0, minPlayers - totalCount)

  return (
    <Button
      data-testid="start-game-button"
      onClick={onClick}
      disabled={disabled}
      variant={disabled ? "outline" : "default"}
      className={`
        w-full px-6 flex flex-col items-center gap-2 h-auto
        ${compact ? 'py-3 min-h-[48px]' : 'py-6 min-h-[56px]'}
        text-base font-black uppercase tracking-[0.2em]
        transition-all duration-300 ease-in-out
        ${disabled
          ? 'disabled:bg-muted disabled:text-muted-foreground disabled:border-border cursor-not-allowed'
          : 'hover:opacity-90'
        }
      `}
    >
      {disabled ? (
        <>
          <span className="text-sm md:text-base">{t("lobby.notEnoughPlayers")}</span>
          <span className="text-xs md:text-sm font-normal">
            {t("lobby.needMorePlayers", { count: needMore, total: totalCount, min: minPlayers })}
          </span>
        </>
      ) : (
        <>
          <span className="text-2xl md:text-2xl">{t("lobby.startGame")}</span>
          {!compact && (
            <span className="text-sm md:text-xs text-primary-foreground/80 font-normal">
              {t("lobby.playersReady", { count: totalCount })}
            </span>
          )}
        </>
      )}
    </Button>
  )
}
